import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

// Collection name
const SKUVIEWER_COLLECTION_BASE = 'pmt2_skuviewer';

/**
 * Fetches entries from a specific document within the skuviewer structure,
 * assuming entries are stored in an 'entries' array field.
 * @param companyName The company name.
 * @param category The category (e.g., 'Client Notes Global', 'Full Margin History').
 * @param docName The name of the document within the category (e.g., 'Notes', 'History').
 * @returns Array of entries or an empty array if not found.
 * @throws Error if Firestore operation fails.
 */
const getSkuViewerEntries = async (companyName: string, category: string, docName: string): Promise<any[]> => {
    if (!companyName || !category || !docName) {
        console.warn(`getSkuViewerEntries called with missing parameters: company=${companyName}, category=${category}, docName=${docName}`);
        return [];
    }
    try {
        const docRef = doc(db, SKUVIEWER_COLLECTION_BASE, companyName, category, docName);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            // Assume entries are stored in an array field named 'entries'
            const data = docSnap.data();
            return Array.isArray(data?.entries) ? data.entries : [];
        } else {
            console.log(`SKU Viewer document not found: ${SKUVIEWER_COLLECTION_BASE}/${companyName}/${category}/${docName}`);
            return [];
        }
    } catch (error) {
        console.error(`Error fetching SKU Viewer entries for ${category}/${docName} (Company: ${companyName}):`, error);
        throw error; // Re-throw error
    }
};


/**
 * Fetches Client Notes Global entries from the pmt2_skuviewer collection.
 */
export const getClientNotesGlobal = (companyName: string): Promise<any[]> => {
    return getSkuViewerEntries(companyName, 'Client Notes Global', 'Notes');
};

/**
 * Fetches Client Notes ASIN entries from the pmt2_skuviewer collection.
 */
export const getClientNotesAsin = (companyName: string): Promise<any[]> => {
    // Note: The original service had the same path as Global Notes.
    // Assuming this might be an error and should perhaps point to a different doc or structure.
    // For now, keeping it the same as the original implementation.
    // If ASIN-specific notes are stored differently, this needs adjustment.
    console.warn("getClientNotesAsin currently points to the same data as getClientNotesGlobal. Verify data structure if ASIN-specific notes are expected.");
    return getSkuViewerEntries(companyName, 'Client Notes ASIN', 'Notes'); // Check if 'Client Notes ASIN' is correct category
};

/**
 * Fetches Full Margin History entries from the pmt2_skuviewer collection.
 */
export const getFullMarginHistory = (companyName: string): Promise<any[]> => {
    return getSkuViewerEntries(companyName, 'Full Margin History', 'History');
};